import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import clsx from 'clsx';
import { useCallback, useContext, useEffect, useState } from 'react';
import { getContentPositionClass, SiteContext } from '../../utils/common';
import useStyles from './BlogHeroBanner.styles';
import Head from 'next/head';
import { jsonLdScriptProps } from 'react-schemaorg';
import { Box, useMediaQuery } from '@mui/material';
import Content from '../Content/Content';
import GenericImage from '../GenericImage/GenericImage';
import { useBackgroundClass } from '../../styles/jss/utils';
import useCommonClasses, { minHeights } from '../../styles/jss/common';
import { useRouter } from 'next/router';
const getMinHeight = (minHeight) => minHeights[minHeight];
const getBreakpoints = (minHeight) => {
    const imageHeights = getMinHeight(minHeight);
    const breakpoints = [
        { breakpoint: 'sm', query: 'max-width', h: imageHeights.xs },
        { breakpoint: 'md', query: 'max-width', h: imageHeights.sm },
        { breakpoint: 'lg', query: 'max-width', h: imageHeights.md },
        { breakpoint: 'xl', query: 'max-width', h: imageHeights.lg },
    ];
    return breakpoints;
};
const getImageOptions = (isFlyingFlowers, minHeight, rgbColor, isFullWidthImage) => {
    const imageHeights = getMinHeight(minHeight);
    const aspectRatio = isFlyingFlowers && isFullWidthImage ? { aspect: '2:1' } : {};
    return isFlyingFlowers ? Object.assign({ h: imageHeights.xl, bg: rgbColor }, aspectRatio) : undefined;
};
const RenderSeoSchema = ({ blogSchemaSEO }) => {
    const getSeoSchema = useCallback((blogSchemaSEO) => {
        try {
            return JSON.parse(blogSchemaSEO);
        }
        catch (error) {
            return '';
        }
    }, []);
    return blogSchemaSEO && getSeoSchema(blogSchemaSEO) ? (_jsx(Head, { children: _jsx("script", Object.assign({}, jsonLdScriptProps(getSeoSchema(blogSchemaSEO)))) })) : null;
};
const BlogHeroBanner = (props) => {
    const { isFlyingFlowers } = useContext(SiteContext);
    const { headingColor = isFlyingFlowers ? 'COBALT' : 'BLACK', message, contentPosition, isFullWidthImage, blogSchemaSEO, headingTag = 'H1', backgroundColor, darknessLevel, heading, readTime, } = props;
    const { classes } = useStyles({ headingColor });
    const router = useRouter();
    const [pageType, setPageType] = useState('blogArticle');
    const { asPath } = router;
    const checkPageType = (url) => {
        if (/^\/blog\/?$/.test(url)) {
            setPageType('blog');
        }
        else {
            setPageType('blogArticle');
        }
    };
    useEffect(() => {
        if (!router.isReady)
            return;
        checkPageType(asPath);
        const handleRouteChange = (url) => {
            checkPageType(url);
        };
        if (router.events) {
            router.events.on('routeChangeComplete', handleRouteChange);
            return () => {
                router.events.off('routeChangeComplete', handleRouteChange);
            };
        }
    }, [router, router.isReady, asPath]);
    const { classes: commonClasses } = useCommonClasses({});
    const isMobileScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const { bgClass, rgbColor } = useBackgroundClass(backgroundColor);
    const positionClass = getContentPositionClass(contentPosition, {
        positionLeft: classes.positionLeft,
        positionRight: classes.positionRight,
        positionCenter: classes.positionCenter,
    });
    const getWrappedText = useCallback((text, type) => {
        return typeof text === 'string' && text.trim()
            ? text.split('/').map((line) => (_jsx("span", { className: clsx(isFlyingFlowers ? classes.headingLine : classes.newHeading, headingColor && type === 'heading' && classes[headingColor], isFlyingFlowers && contentPosition === 'CENTER' ? classes.centerHeading : ''), children: line.trim() }, `${line}_index`)))
            : text;
    }, [classes, contentPosition, headingColor, isFlyingFlowers]);
    const minHeight = !!isFlyingFlowers && isFullWidthImage ? 'LARGE' : 'BLOG';
    let messageClass;
    if (isFlyingFlowers) {
        messageClass = classes.messageFF;
    }
    else if (pageType === 'blog') {
        messageClass = classes.blogPage;
    }
    else if (pageType === 'blogArticle') {
        messageClass = classes.blogArticlePage;
    }
    else {
        messageClass = classes.message;
    }
    const getGenericImageClassNames = () => [
        !isFlyingFlowers ? classes.image : '',
        !isFlyingFlowers ? classes.fullWidth : '',
        isFlyingFlowers && !isFullWidthImage ? classes.ffImagePosition : '',
        isFlyingFlowers ? classes.ffImage : '',
        isFlyingFlowers && isFullWidthImage ? classes.fullWidthCoverFF : '',
        isFlyingFlowers && isFullWidthImage ? commonClasses.minHeightLarge : commonClasses.minHeightBlog,
        commonClasses.image,
    ];
    const renderGenericImage = () => {
        var _a;
        return (_jsx(GenericImage, Object.assign({}, props, { fullWidthImage: true, classNames: getGenericImageClassNames(), renderSquareImage: !isFlyingFlowers || (isFlyingFlowers && isFullWidthImage), renderSquaredImagesOptions: !isFlyingFlowers || (isFlyingFlowers && isFullWidthImage), imageOptions: getImageOptions(!!isFlyingFlowers, minHeight, rgbColor, isFullWidthImage), addDarkness: !isFlyingFlowers || !!(!backgroundColor && isFlyingFlowers), addBackgroundColorForImage: isFlyingFlowers && !isFullWidthImage, backgroundColor: rgbColor, isPriority: true, breakpoints: isFlyingFlowers && !isFullWidthImage ? getBreakpoints(minHeight) : undefined, darknessLevel: darknessLevel, showImageSpacer: !!((_a = props.image) === null || _a === void 0 ? void 0 : _a.url), minHeight: isFlyingFlowers && isFullWidthImage ? 'LARGE' : 'BLOG' })));
    };
    const renderContent = () => (_jsx(Content, Object.assign({}, props, { headingTag: headingTag, headingColor: headingColor, heading: classes[headingColor] ? getWrappedText(heading, 'heading') : heading, headingClasses: [classes.blogHeading, isFlyingFlowers ? classes.blogHeadingFF : '', classes.heading], messageClasses: [messageClass], contentInnerClasses: [classes.contentInner], textAlignment: isFlyingFlowers && contentPosition === 'CENTER' ? 'CENTER' : 'LEFT', containerClasses: [
            isFlyingFlowers ? classes.ffContent : classes.content,
            isFlyingFlowers && !isFullWidthImage && contentPosition === 'CENTER' ? classes.ffContentPosition : '',
        ], wrapText: true, wrapTextForMessage: !isFlyingFlowers, isFlyingFlowers: isFlyingFlowers, readTime: !isFlyingFlowers && readTime && pageType !== 'blog' && readTime > 0 ? readTime : undefined, pageType: pageType })));
    const renderMessage = () => message && isFlyingFlowers ? (_jsx(Box, { component: 'p', className: clsx(classes.message, classes.outerMessage, contentPosition === 'CENTER' ? classes.textCenter : ''), children: message })) : null;
    return (_jsxs(_Fragment, { children: [_jsxs(Box, { className: clsx(classes.container, classes.mobilePositionOver, classes.blogContainer, classes.fontSohne, isFlyingFlowers && isFullWidthImage ? commonClasses.minHeightLarge : commonClasses.minHeightBlog, isFlyingFlowers && isMobileScreen && classes.ffMobileheight, positionClass !== null && positionClass !== void 0 ? positionClass : classes.positionLeft, isFlyingFlowers && isMobileScreen && !isFullWidthImage ? classes.ffMobileContent : '', isFlyingFlowers && isFullWidthImage ? classes.ffPosition : '', bgClass), children: [renderGenericImage(), renderContent()] }), renderMessage(), blogSchemaSEO && _jsx(RenderSeoSchema, { blogSchemaSEO: blogSchemaSEO })] }));
};
export default BlogHeroBanner;
